import {
  type CuttingRoomResponse,
  cuttingRoomBridge,
} from './cutting_room_bridge';

export type { CuttingRoomResponse };

/**
 * Send an `open_project` command to load a project in the embedded player.
 */
export function openProject(projectId: string): void {
  cuttingRoomBridge.post('open_project', { projectId });
}

export type ProjectBinItem = {
  url: string;
  resourceId?: string;
  title: string;
  contentType: string;
};

type CreateProjectPayload = {
  title: string;
  fps: number;
  projectId?: string;
  projectBinItems?: ProjectBinItem[];
};

type CreateProjectResponseData = {
  action: string;
  message: string;
  projectId: string;
  projectUrl: string;
};

type AddToProjectBinResponseData = {
  message: {
    action: 'add_to_project_bin';
    payload: { projectBinItems: (string | ProjectBinItem)[] };
    messageId?: string;
  };
  status: 'ok' | 'error';
  error?: string;
};

/**
 * Add items to the currently open CuttingRoom project's bin via the
 * `add_to_project_bin` postMessage action.
 *
 * Returns the response from CuttingRoom. Throws if the request fails.
 */
export async function addToProjectBin(
  projectBinItems: (string | ProjectBinItem)[],
): Promise<AddToProjectBinResponseData> {
  const { response } = await cuttingRoomBridge.send('add_to_project_bin', {
    projectBinItems,
  });

  const status = response.status as string | undefined;
  const error = response.error as string | undefined;

  if (status !== 'ok') {
    throw new Error(
      `Failed to add items to project bin: ${error ?? 'unknown error'}`,
    );
  }

  return response as unknown as AddToProjectBinResponseData;
}

/**
 * Create a new CuttingRoom project via the `call_api` postMessage action.
 *
 * Uses the CuttingRoom embedded `call_api` bridge which proxies the request
 * through the authenticated iframe session.
 *
 * @see https://docs.cuttingroom.com/reference/create-new-project.md
 * @see https://docs.cuttingroom.com/reference/calling-backend-api-on-behalf-of-logged-in-user.md
 */
export async function createProject(
  payload: CreateProjectPayload,
): Promise<CreateProjectResponseData> {
  const { response } = await cuttingRoomBridge.send('call_api', {
    path: '/projects',
    method: 'POST',
    body: payload,
  });

  const status = response.status as string | undefined;
  const data = response.data as CreateProjectResponseData | undefined;
  const error = response.error as string | undefined;

  if (status !== 'ok' || !data?.projectId) {
    throw new Error(
      `CuttingRoom project creation failed: ${error ?? 'unknown error'}`,
    );
  }

  return data;
}
